/**
 * PlanOrchestrationDashboard - BCM Manager Dashboard for BCP and IT-DRP Plans
 * Provides consolidated view of all recovery plans with tracking and notifications
 * ISO 22301 Clause 8.4 - Business Continuity Plans
 * 
 * Features real-time updates via WebSocket
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useWebSocket, WS_EVENTS } from "../hooks/useWebSocket";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  LayoutDashboard,
  FileCheck,
  Server,
  Cloud,
  Building2,
  Truck,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Bell,
  Send,
  Target,
  TrendingUp,
  Calendar,
  ChevronRight,
  Eye,
  Zap,
  Shield,
  Play,
  Layers,
  Wifi,
  WifiOff,
} from "lucide-react";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-slate-100 text-slate-700", icon: Clock },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-700", icon: RefreshCw },
  submitted: { label: "Submitted", color: "bg-amber-100 text-amber-700", icon: FileCheck },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle },
  revision_required: { label: "Needs Revision", color: "bg-sky-100 text-sky-700", icon: AlertTriangle },
};

const PLAN_TYPE_CONFIG = {
  bcp: { label: "BCP", color: "bg-emerald-100 text-emerald-700 border-emerald-300", description: "Business Continuity Plan" },
  it_drp: { label: "IT-DRP", color: "bg-blue-100 text-blue-700 border-blue-300", description: "IT Disaster Recovery Plan" },
};

const TEAM_CONFIG = {
  it_team: { label: "IT Team", icon: Server, color: "bg-blue-100" },
  devops_team: { label: "DevOps", icon: Cloud, color: "bg-cyan-100" },
  facility_team: { label: "Facility", icon: Building2, color: "bg-purple-100" },
  vendor_team: { label: "Vendor", icon: Truck, color: "bg-sky-100" },
  business_process_owner: { label: "Process Owner", icon: Users, color: "bg-emerald-100" },
};

export default function PlanOrchestrationDashboard({ embedded = false }) {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState({ bcp: [], it_drp: [] });
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [showOrchestrationDialog, setShowOrchestrationDialog] = useState(false);
  const [integratedPlan, setIntegratedPlan] = useState(null);
  const [sending, setSending] = useState(false);
  
  // WebSocket for real-time updates
  const { isConnected, subscribe } = useWebSocket();

  // Redirect business users
  useEffect(() => {
    if (user && (user.role === "process_manager" || user.role === "process_manager")) {
      toast.error("Plan Orchestration is only available to BCM Managers");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [orchestrationRes, statsRes] = await Promise.all([
        api.get("/emergency-response/orchestration/plans"),
        api.get("/recovery-tasks/stats/summary"),
      ]);
      
      // Process plans by business unit and team
      const bcpPlans = orchestrationRes.data.bcp_plans?.by_business_unit || {};
      const drpPlans = orchestrationRes.data.drp_plans?.by_team || {};
      
      // Flatten for display
      const flatBcp = Object.entries(bcpPlans).flatMap(([unit, plans]) => 
        plans.map(p => ({ ...p, business_unit: unit }))
      );
      const flatDrp = Object.entries(drpPlans).flatMap(([team, plans]) => 
        plans.map(p => ({ ...p, team_type: team }))
      );
      
      setPlans({
        bcp: flatBcp,
        it_drp: flatDrp,
        summary: orchestrationRes.data.summary,
      });
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load plan data");
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Subscribe to WebSocket events for real-time updates
  useEffect(() => {
    // Re-fetch data when plans are approved/rejected
    const unsubApproved = subscribe(WS_EVENTS.PLAN_APPROVED, () => {
      fetchData();
    });
    
    const unsubRejected = subscribe(WS_EVENTS.PLAN_REJECTED, () => {
      fetchData();
    });
    
    const unsubRevision = subscribe(WS_EVENTS.PLAN_REVISION_REQUIRED, () => {
      fetchData();
    });
    
    const unsubIncident = subscribe(WS_EVENTS.INCIDENT_ACTIVATED, () => {
      fetchData();
    });
    
    const unsubCrisis = subscribe(WS_EVENTS.CRISIS_ALERT_SENT, () => {
      fetchData();
    });
    
    return () => {
      unsubApproved();
      unsubRejected();
      unsubRevision();
      unsubIncident();
      unsubCrisis();
    };
  }, [subscribe, fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSendReminders = async () => {
    setSending(true);
    try {
      // Send reminders for overdue/pending tasks
      await api.post("/recovery-tasks/send-reminders", {
        task_ids: selectedPlans.length > 0 ? selectedPlans : undefined,
      });
      toast.success("Reminders sent successfully");
      setShowReminderDialog(false);
      setSelectedPlans([]);
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setSending(false);
    }
  };

  const handleGenerateIntegratedPlan = async () => {
    if (selectedPlans.length === 0) {
      toast.error("Please select at least one plan");
      return;
    }
    
    setSending(true);
    try {
      const bcpIds = selectedPlans.filter(id => plans.bcp.some(p => p.id === id));
      const drpIds = selectedPlans.filter(id => plans.it_drp.some(p => p.id === id));
      
      const response = await api.post("/emergency-response/orchestration/integrate", null, {
        params: {
          selected_bcp_ids: bcpIds,
          selected_drp_ids: drpIds,
          incident_scope: "site_wide",
        }
      });
      
      setIntegratedPlan(response.data);
      toast.success("Integrated plan generated");
    } catch (error) {
      toast.error("Failed to generate integrated plan");
    } finally {
      setSending(false);
    }
  };

  const togglePlanSelection = (planId) => {
    setSelectedPlans(prev => 
      prev.includes(planId) 
        ? prev.filter(id => id !== planId)
        : [...prev, planId]
    );
  };

  const getOverduePlans = () => {
    const allPlans = [...plans.bcp, ...plans.it_drp];
    return allPlans.filter(p => p.is_overdue || p.status === "pending");
  };

  const getCompletionRate = (planType) => {
    const planList = plans[planType] || [];
    if (planList.length === 0) return 0;
    const approved = planList.filter(p => p.status === "approved").length;
    return Math.round((approved / planList.length) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <div className="w-8 h-8 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="plan-orchestration-dashboard">
      {/* Header */}
      {!embedded && (
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-sky-100 text-sky-700">DO Phase</Badge>
            <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">ISO 22301:8.4</Badge>
            {isConnected ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="ws-connected">
                <Wifi className="w-3 h-3 mr-1" />
                Live
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200" data-testid="ws-disconnected">
                <WifiOff className="w-3 h-3 mr-1" />
                Offline
              </Badge>
            )}
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Plan Orchestration Dashboard</h1>
          <p className="text-slate-600 mt-1">
            Consolidated view of all BCP and IT-DRP plans across the organization
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowReminderDialog(true)}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            <Bell className="w-4 h-4 mr-2" />
            Send Reminders
          </Button>
          <Button 
            onClick={() => setShowOrchestrationDialog(true)}
            className="bg-sky-700 hover:bg-sky-800"
            disabled={selectedPlans.length === 0}
          >
            <Layers className="w-4 h-4 mr-2" />
            Orchestrate Plans ({selectedPlans.length})
          </Button>
        </div>
      </div>
      )}

      {/* Summary Stats */}
      <div className="grid md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Plans</p>
                <p className="text-2xl font-bold text-slate-900">
                  {plans.summary?.total_plans || (plans.bcp.length + plans.it_drp.length)}
                </p>
              </div>
              <LayoutDashboard className="w-8 h-8 text-sky-700 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">BCP Plans</p>
                <p className="text-2xl font-bold text-emerald-600">{plans.bcp.length}</p>
              </div>
              <Users className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
            <Progress value={getCompletionRate('bcp')} className="mt-2 h-2" />
            <p className="text-xs text-slate-500 mt-1">{getCompletionRate('bcp')}% approved</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">IT-DRP Plans</p>
                <p className="text-2xl font-bold text-blue-600">{plans.it_drp.length}</p>
              </div>
              <Server className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
            <Progress value={getCompletionRate('it_drp')} className="mt-2 h-2" />
            <p className="text-xs text-slate-500 mt-1">{getCompletionRate('it_drp')}% approved</p>
          </CardContent>
        </Card>
        <Card className={stats?.overdue > 0 ? "border-red-300" : ""}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{stats?.overdue || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Completion</p>
                <p className="text-2xl font-bold text-sky-700">{stats?.completion_rate || 0}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-sky-700 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <LayoutDashboard className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="bcp" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            BCP Plans
            <Badge variant="secondary" className="ml-1">{plans.bcp.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="it_drp" className="flex items-center gap-2">
            <Server className="w-4 h-4" />
            IT-DRP Plans
            <Badge variant="secondary" className="ml-1">{plans.it_drp.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Timeline
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* BCP Summary by Business Unit */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-600" />
                  BCP by Business Unit
                </CardTitle>
                <CardDescription>Business Continuity Plans organized by department</CardDescription>
              </CardHeader>
              <CardContent>
                {plans.bcp.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No BCP plans available</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(
                      plans.bcp.reduce((acc, plan) => {
                        const unit = plan.business_unit || "Unknown";
                        if (!acc[unit]) acc[unit] = [];
                        acc[unit].push(plan);
                        return acc;
                      }, {})
                    ).map(([unit, unitPlans]) => (
                      <div key={unit} className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-emerald-800">{unit}</h4>
                          <Badge className="bg-emerald-100 text-emerald-700">
                            {unitPlans.filter(p => p.status === "approved").length}/{unitPlans.length} approved
                          </Badge>
                        </div>
                        <div className="space-y-1">
                          {unitPlans.slice(0, 3).map(plan => (
                            <div key={plan.id} className="flex items-center justify-between text-sm">
                              <span className="text-slate-700 truncate">{plan.title}</span>
                              <Badge className={STATUS_CONFIG[plan.status]?.color || "bg-slate-100"}>
                                {STATUS_CONFIG[plan.status]?.label || plan.status}
                              </Badge>
                            </div>
                          ))}
                          {unitPlans.length > 3 && (
                            <p className="text-xs text-slate-500">+{unitPlans.length - 3} more</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* IT-DRP Summary by Team */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="w-5 h-5 text-blue-600" />
                  IT-DRP by Team
                </CardTitle>
                <CardDescription>IT Disaster Recovery Plans by responsible team</CardDescription>
              </CardHeader>
              <CardContent>
                {plans.it_drp.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No IT-DRP plans available</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(
                      plans.it_drp.reduce((acc, plan) => {
                        const team = plan.team_type || "it_team";
                        if (!acc[team]) acc[team] = [];
                        acc[team].push(plan);
                        return acc;
                      }, {})
                    ).map(([team, teamPlans]) => {
                      const teamConfig = TEAM_CONFIG[team] || TEAM_CONFIG.it_team;
                      const TeamIcon = teamConfig.icon;
                      return (
                        <div key={team} className={`p-3 rounded-lg border ${teamConfig.color}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <TeamIcon className="w-4 h-4" />
                              <h4 className="font-medium">{teamConfig.label}</h4>
                            </div>
                            <Badge className="bg-blue-100 text-blue-700">
                              {teamPlans.filter(p => p.status === "approved").length}/{teamPlans.length} approved
                            </Badge>
                          </div>
                          <div className="space-y-1">
                            {teamPlans.slice(0, 3).map(plan => (
                              <div key={plan.id} className="flex items-center justify-between text-sm">
                                <span className="text-slate-700 truncate">{plan.title}</span>
                                <Badge className={STATUS_CONFIG[plan.status]?.color || "bg-slate-100"}>
                                  {STATUS_CONFIG[plan.status]?.label || plan.status}
                                </Badge>
                              </div>
                            ))}
                            {teamPlans.length > 3 && (
                              <p className="text-xs text-slate-500">+{teamPlans.length - 3} more</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Overdue/Action Required */}
          {getOverduePlans().length > 0 && (
            <Card className="mt-6 border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" />
                  Action Required
                </CardTitle>
                <CardDescription>Plans that need immediate attention</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {getOverduePlans().slice(0, 5).map(plan => (
                    <div key={plan.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedPlans.includes(plan.id)}
                          onCheckedChange={() => togglePlanSelection(plan.id)}
                        />
                        <div>
                          <p className="font-medium text-slate-900">{plan.title}</p>
                          <p className="text-xs text-slate-500">
                            {plan.business_unit || TEAM_CONFIG[plan.team_type]?.label}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={STATUS_CONFIG[plan.status]?.color}>
                          {STATUS_CONFIG[plan.status]?.label}
                        </Badge>
                        <Button size="sm" variant="outline" onClick={() => navigate("/recovery-task-review")}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* BCP Plans Tab */}
        <TabsContent value="bcp">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Business Continuity Plans</CardTitle>
                  <CardDescription>Recovery plans created by Process Owners</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const allBcpIds = plans.bcp.filter(p => p.status === "approved").map(p => p.id);
                    setSelectedPlans(prev => {
                      const hasAll = allBcpIds.every(id => prev.includes(id));
                      return hasAll 
                        ? prev.filter(id => !allBcpIds.includes(id))
                        : [...new Set([...prev, ...allBcpIds])];
                    });
                  }}
                >
                  Select All Approved
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {plans.bcp.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No BCP plans available</p>
                  <p className="text-sm text-slate-400">Process Owners need to create recovery packages first</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {plans.bcp.map(plan => (
                    <div 
                      key={plan.id} 
                      className={`p-4 border rounded-lg transition-all ${
                        selectedPlans.includes(plan.id) ? "border-emerald-500 bg-emerald-50" : "hover:border-emerald-300"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <Checkbox
                          checked={selectedPlans.includes(plan.id)}
                          onCheckedChange={() => togglePlanSelection(plan.id)}
                          disabled={plan.status !== "approved"}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{plan.title}</h4>
                            <Badge className={PLAN_TYPE_CONFIG.bcp.color}>BCP</Badge>
                            <Badge className={STATUS_CONFIG[plan.status]?.color}>
                              {STATUS_CONFIG[plan.status]?.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600">
                            Business Unit: {plan.business_unit} | 
                            RTO: {plan.rto_hours || plan.team_bia_reference?.rto_hours || "N/A"}h
                          </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => navigate("/recovery-task-review")}>
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* IT-DRP Plans Tab */}
        <TabsContent value="it_drp">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>IT Disaster Recovery Plans</CardTitle>
                  <CardDescription>Technical recovery plans from IT and DevOps teams</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const allDrpIds = plans.it_drp.filter(p => p.status === "approved").map(p => p.id);
                    setSelectedPlans(prev => {
                      const hasAll = allDrpIds.every(id => prev.includes(id));
                      return hasAll 
                        ? prev.filter(id => !allDrpIds.includes(id))
                        : [...new Set([...prev, ...allDrpIds])];
                    });
                  }}
                >
                  Select All Approved
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {plans.it_drp.length === 0 ? (
                <div className="text-center py-12">
                  <Server className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No IT-DRP plans available</p>
                  <p className="text-sm text-slate-400">IT and DevOps teams need to create recovery tasks first</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {plans.it_drp.map(plan => {
                    const teamConfig = TEAM_CONFIG[plan.team_type] || TEAM_CONFIG.it_team;
                    const TeamIcon = teamConfig.icon;
                    return (
                      <div 
                        key={plan.id} 
                        className={`p-4 border rounded-lg transition-all ${
                          selectedPlans.includes(plan.id) ? "border-blue-500 bg-blue-50" : "hover:border-blue-300"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <Checkbox
                            checked={selectedPlans.includes(plan.id)}
                            onCheckedChange={() => togglePlanSelection(plan.id)}
                            disabled={plan.status !== "approved"}
                          />
                          <div className={`p-2 rounded-lg ${teamConfig.color}`}>
                            <TeamIcon className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{plan.title}</h4>
                              <Badge className={PLAN_TYPE_CONFIG.it_drp.color}>IT-DRP</Badge>
                              <Badge className={STATUS_CONFIG[plan.status]?.color}>
                                {STATUS_CONFIG[plan.status]?.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600">
                              Team: {teamConfig.label} | 
                              RTO: {plan.rto_hours || plan.team_bia_reference?.rto_hours || "N/A"}h
                            </p>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => navigate("/recovery-task-review")}>
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>Recovery Timeline</CardTitle>
              <CardDescription>Phased recovery approach based on RTO priorities</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Phase 1: Critical (0-4h) */}
                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-5 h-5 text-red-600" />
                    <h4 className="font-semibold text-red-700">Phase 1: Initial Response (0-4 hours)</h4>
                  </div>
                  <div className="space-y-2">
                    {[...plans.bcp, ...plans.it_drp]
                      .filter(p => (p.rto_hours || p.team_bia_reference?.rto_hours || 999) <= 4)
                      .map(plan => (
                        <div key={plan.id} className="flex items-center justify-between p-2 bg-red-50 rounded">
                          <span className="text-sm">{plan.title}</span>
                          <Badge className={plan.is_bcp ? PLAN_TYPE_CONFIG.bcp.color : PLAN_TYPE_CONFIG.it_drp.color}>
                            {plan.is_bcp ? "BCP" : "IT-DRP"}
                          </Badge>
                        </div>
                      ))}
                    {[...plans.bcp, ...plans.it_drp].filter(p => (p.rto_hours || p.team_bia_reference?.rto_hours || 999) <= 4).length === 0 && (
                      <p className="text-sm text-slate-500">No critical plans in this phase</p>
                    )}
                  </div>
                </div>

                {/* Phase 2: Urgent (4-24h) */}
                <div className="border-l-4 border-sky-700 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-sky-700" />
                    <h4 className="font-semibold text-sky-700">Phase 2: Stabilization (4-24 hours)</h4>
                  </div>
                  <div className="space-y-2">
                    {[...plans.bcp, ...plans.it_drp]
                      .filter(p => {
                        const rto = p.rto_hours || p.team_bia_reference?.rto_hours || 999;
                        return rto > 4 && rto <= 24;
                      })
                      .map(plan => (
                        <div key={plan.id} className="flex items-center justify-between p-2 bg-sky-50 rounded">
                          <span className="text-sm">{plan.title}</span>
                          <Badge className={plan.is_bcp ? PLAN_TYPE_CONFIG.bcp.color : PLAN_TYPE_CONFIG.it_drp.color}>
                            {plan.is_bcp ? "BCP" : "IT-DRP"}
                          </Badge>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Phase 3: Recovery (24-72h) */}
                <div className="border-l-4 border-amber-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-5 h-5 text-amber-600" />
                    <h4 className="font-semibold text-amber-700">Phase 3: Recovery (24-72 hours)</h4>
                  </div>
                  <div className="space-y-2">
                    {[...plans.bcp, ...plans.it_drp]
                      .filter(p => {
                        const rto = p.rto_hours || p.team_bia_reference?.rto_hours || 999;
                        return rto > 24 && rto <= 72;
                      })
                      .map(plan => (
                        <div key={plan.id} className="flex items-center justify-between p-2 bg-amber-50 rounded">
                          <span className="text-sm">{plan.title}</span>
                          <Badge className={plan.is_bcp ? PLAN_TYPE_CONFIG.bcp.color : PLAN_TYPE_CONFIG.it_drp.color}>
                            {plan.is_bcp ? "BCP" : "IT-DRP"}
                          </Badge>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Phase 4: Restoration (72h+) */}
                <div className="border-l-4 border-emerald-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <h4 className="font-semibold text-emerald-700">Phase 4: Restoration (72+ hours)</h4>
                  </div>
                  <div className="space-y-2">
                    {[...plans.bcp, ...plans.it_drp]
                      .filter(p => (p.rto_hours || p.team_bia_reference?.rto_hours || 999) > 72)
                      .map(plan => (
                        <div key={plan.id} className="flex items-center justify-between p-2 bg-emerald-50 rounded">
                          <span className="text-sm">{plan.title}</span>
                          <Badge className={plan.is_bcp ? PLAN_TYPE_CONFIG.bcp.color : PLAN_TYPE_CONFIG.it_drp.color}>
                            {plan.is_bcp ? "BCP" : "IT-DRP"}
                          </Badge>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Send Reminders Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-600" />
              Send Reminders
            </DialogTitle>
            <DialogDescription>
              Send notification reminders to owners with pending or overdue recovery plans
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600">
              This will send in-app notifications to:
            </p>
            <ul className="mt-2 space-y-1 text-sm">
              <li className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span>{stats?.overdue || 0} overdue tasks</span>
              </li>
              <li className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />
                <span>{stats?.by_status?.pending || 0} pending tasks</span>
              </li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReminderDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendReminders} 
              disabled={sending}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {sending ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Reminders
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Orchestration Dialog */}
      <Dialog open={showOrchestrationDialog} onOpenChange={setShowOrchestrationDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-sky-700" />
              Plan Orchestration
            </DialogTitle>
            <DialogDescription>
              Generate an integrated recovery plan from selected BCP and IT-DRP plans
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="mb-4 p-3 bg-sky-50 rounded-lg">
              <p className="text-sm text-sky-700">
                <strong>{selectedPlans.length}</strong> plans selected for orchestration
              </p>
            </div>

            {!integratedPlan ? (
              <Button 
                onClick={handleGenerateIntegratedPlan} 
                disabled={sending}
                className="w-full bg-sky-700 hover:bg-sky-800"
              >
                {sending ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Generate Integrated Plan
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <h4 className="font-semibold text-emerald-800 mb-2">
                    {integratedPlan.name}
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">BCP Plans</p>
                      <p className="font-medium">{integratedPlan.summary?.total_bcp_plans || 0}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">DRP Plans</p>
                      <p className="font-medium">{integratedPlan.summary?.total_drp_plans || 0}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Est. Recovery</p>
                      <p className="font-medium">{integratedPlan.summary?.estimated_full_recovery_hours || 0}h</p>
                    </div>
                  </div>
                </div>

                {/* Phases */}
                {integratedPlan.phases?.map((phase, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium">{phase.phase}</h5>
                      <Badge variant="outline">{phase.time_window}</Badge>
                    </div>
                    <div className="space-y-1">
                      {phase.tasks?.map((task, i) => (
                        <div key={i} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                          <span>{task.plan_name}</span>
                          <Badge className={task.plan_type === "BCP" ? PLAN_TYPE_CONFIG.bcp.color : PLAN_TYPE_CONFIG.it_drp.color}>
                            {task.plan_type}
                          </Badge>
                        </div>
                      ))}
                      {phase.tasks?.length === 0 && (
                        <p className="text-sm text-slate-500">No tasks in this phase</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowOrchestrationDialog(false);
              setIntegratedPlan(null);
            }}>
              Close
            </Button>
            {integratedPlan && (
              <Button 
                onClick={() => navigate("/emergency-response")}
                className="bg-red-600 hover:bg-red-700"
              >
                <Shield className="w-4 h-4 mr-2" />
                Go to Emergency Response
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
